import React, { useState, useEffect } from 'react';
import { Modal } from '@mantine/core';
import Icon from 'src/components/common/Icons/Icons/Icon';
import { CustomButton } from 'src/components/common/Buttons/CustomButton';
import FloatingInput from '../Inputs/FloatingInput';
import { sendPublicChatFeedback, FeedbackApiResponse } from 'src/api/publicChat';

interface EmailModalProps {
    opened: boolean;
    onClose: () => void;
    previousEmail:string;
    name:string;
    conversation_id: string;
}


const FeedbackModal: React.FC<EmailModalProps> = ({ opened, onClose,previousEmail,name, conversation_id}) => {
    const [email, setEmail] = useState(previousEmail);
    const [feedback, setFeedback] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState('');

    // Focus email input when modal opens
    useEffect(() => {
        if (opened && !isSuccess) {
            // Small delay to ensure modal is fully rendered
            const timer = setTimeout(() => {
                // Focus the email input using its label as selector
                const emailInput = document.querySelector('input[aria-label="Email Address"]') as HTMLInputElement;
                if (emailInput) {
                    emailInput.focus();
                }
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [opened, isSuccess]);

    useEffect(() => {
        resetForm()
    }, [opened]);

    // Reset form function
    const resetForm = () => {
        setEmail(previousEmail);
        setFeedback('');
        setIsSubmitting(false);
        setIsSuccess(false);
        setError('');
    };

    // Handle close with reset
    const handleClose = () => {
        resetForm();
        onClose();
    };

    const validateEmail = (email: string) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const handleSubmit = async () => {
        // Reset error state
        setError('');

        // Validate email
        if (!validateEmail(email)) {
            setError('Please enter a valid email address');
            return;
        }

        // Validate feedback
        if (feedback.trim().length < 5) {
            setError('Please provide some feedback (at least 5 characters)');
            return;
        }
        if (feedback.trim().length > 1000) {
            setError('Feedback is too long (maximum 1000 characters allowed)');
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await sendPublicChatFeedback({
                conversation_id: conversation_id,
                message: feedback,
                name: name,
                email: email,
            });

            if (response.success) {
                setIsSuccess(true);

                setTimeout(() => {
                    onClose();
                }, 2000);
            } else {
                setError(response.message || 'Failed to submit feedback.');
            }
        } catch (error: any) {
            console.error('Error submitting form:', error);
            setError(error.message || 'Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={`We appreciate your feedback ${name}`}
            size="xl"
            centered
            trapFocus={false}
            styles={{
                title: {
                    fontWeight: 'bold',
                    fontSize: '1.5rem',
                    color: '#29748F',
                    textAlign: 'center',
                    flexGrow: 1
                },
                header: {
                    paddingTop: '20px'
                },
                root: {
                    borderRadius: '16px'
                },
                body: {
                    padding: '20px 30px !important'
                }
            }}
        >
            <div className="flex flex-col space-y-4 sm:space-y-6">
                {!isSuccess ? (
                    <>
                        <div className="space-y-4 sm:space-y-6">
                            <div className='flex flex-col sm:flex-row gap-y-2 sm:gap-y-0 sm:gap-x-3 items-start sm:items-end'>
                                <FloatingInput
                                    value={email}
                                    setValue={setEmail}
                                    label="Email Address"
                                    maxLength={30}
                                    disabled={isSubmitting}
                                    disableFloatingLabel={true}
                                    width="w-full"
                                    placeholder="Enter your email address"
                                />
                            </div>

                            <div>
                                <textarea
                                    id="feedback"
                                    value={feedback}
                                    onChange={(e) => setFeedback(e.target.value)}
                                    placeholder="What did you like about MYAi? Got any ideas to make it even better? We're all ears!"
                                    maxLength={1000}
                                    rows={4}
                                    className="w-full border border-gray-300 rounded-lg px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#29748F] resize-none"
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-xs sm:text-sm">
                                {error}
                            </div>
                        )}

                        <div className="flex justify-center sm:justify-end pt-1 sm:pt-2">
                            <CustomButton
                                onClick={handleSubmit}
                                variant="default"
                                size="lg"
                                width="w-full sm:w-1/3"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <div className="flex items-center justify-center">
                                        <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
                                        <span className="text-white text-xs sm:text-sm">Submitting...</span>
                                    </div>
                                ) : (
                                    <span className="text-white text-xs sm:text-sm">Submit</span>
                                )}
                            </CustomButton>
                        </div>
                    </>
                ) : (
                    <div className="text-center py-6 sm:py-8">
                        <div className="mx-auto flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-green-100 mb-3 sm:mb-4">
                            <Icon name="check" size="20" color="#22c55e" className="sm:!w-6 sm:!h-6" />
                        </div>
                        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-1 sm:mb-2">Thank you!</h3>
                        <p className="text-sm sm:text-base text-gray-600">
                            We've received your information. We'll be in touch soon!
                        </p>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default FeedbackModal;